import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { authGet, authDelete, authPost, authPut, API, BACKEND_URL } from "@/lib/api";
import {
  Plus, MapPin, Sparkles, LayoutGrid, Filter, ChevronRight, User, Home,
  Calendar, Users, Shield, Trash2, Check, X, CheckCircle, XCircle, Upload, HelpCircle
} from "lucide-react";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import { toast, Toaster } from "sonner";
import { format, parseISO, differenceInDays, differenceInHours, differenceInMinutes } from "date-fns";
import { de } from "date-fns/locale";

const THEME = {
  bg: "var(--brand-background)", // Light warm background
  bgCard: "#FFFFFF",
  accent: "var(--brand-accent)",
  text: "var(--brand-text)",
  textMuted: "var(--brand-text)60",
  fontSerif: "'Cormorant Garamond', serif",
  fontSans: "'Plus Jakarta Sans', sans-serif"
};

const EVENT_TYPE_TRANSLATIONS = {
  "wedding": "Hochzeit",
  "birthday": "Geburtstag",
  "party": "Party",
  "bachelorette": "JGA",
  "graduation": "Abschluss",
  "anniversary": "Jubiläum",
  "babyshower": "Babyparty",
  "corporate": "Firmenfeier",
  "christmas": "Weihnachten",
  "newyear": "Silvester",
  "new_year": "Silvester",
  "easter": "Ostern",
  "valentine": "Valentinstag",
  "baby_shower": "Babyparty",
  "baby": "Babyparty",
  "other": "Event"
};

const EVENT_TYPE_IMAGES = {
  // English Defaults
  wedding: "/images/headers/wedding.png",
  birthday: "/images/headers/birthday_new.png",
  party: "/images/headers/party_new.png",
  bachelorette: "/images/headers/bachelorette.png",
  graduation: "/images/headers/graduation.png",
  anniversary: "/images/headers/anniversary.png",
  babyshower: "/images/headers/baby-shower.png",
  baby_shower: "/images/headers/baby-shower.png",
  baby: "/images/headers/baby-shower.png",
  corporate: "/images/headers/corporate.png",
  christmas: "/images/headers/christmas.png",
  newyear: "/images/headers/newyear.png",
  new_year: "/images/headers/newyear.png",
  easter: "/images/headers/easter.png",
  valentine: "/images/headers/valentine.png",
  other: "/images/headers/party_new.png",

  // German & Aliases
  hochzeit: "/images/headers/wedding.png",
  geburtstag: "/images/headers/birthday_new.png",
  feier: "/images/headers/party_new.png",
  jga: "/images/headers/bachelorette.png",
  junggesellenabschied: "/images/headers/bachelorette.png",
  abschluss: "/images/headers/graduation.png",
  abschlussfeier: "/images/headers/graduation.png",
  jubilaeum: "/images/headers/anniversary.png",
  jubiläum: "/images/headers/anniversary.png",
  babyparty: "/images/headers/baby-shower.png",
  taufe: "/images/headers/baby-shower.png",
  gender_reveal: "/images/headers/baby-shower.png",
  firma: "/images/headers/corporate.png",
  firmenevent: "/images/headers/corporate.png",
  business: "/images/headers/corporate.png",
  weihnachten: "/images/headers/christmas.png",
  weihnachtsfeier: "/images/headers/christmas.png",
  silvester: "/images/headers/newyear.png",
  ostern: "/images/headers/easter.png",
  valentinstag: "/images/headers/valentine.png",
  sonstiges: "/images/headers/party_new.png",
  individuell: "/images/headers/party_new.png"
};

export default function Dashboard({ user }) {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [participations, setParticipations] = useState([]); // NEW
  const [loading, setLoading] = useState(true);
  const [nextEvent, setNextEvent] = useState(null);
  const [logo, setLogo] = useState(null);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0 });
  const [decliningEventId, setDecliningEventId] = useState(null);
  const [declineMessage, setDeclineMessage] = useState("");
  const [activeTab, setActiveTab] = useState('my-events');
  // Cache buster for images (initialized once per session)
  const [cacheBuster] = useState(Date.now());

  // Tutorial setup
  const startTutorial = () => {
    const driverObj = driver({
      showProgress: true,
      animate: true,
      nextBtnText: 'Weiter',
      prevBtnText: 'Zurück',
      doneBtnText: 'Fertig',
      steps: [
        {
          element: '.tour-new-event',
          popover: { title: 'Neues Event', description: 'Herzlich willkommen! Hier kannst du mit einem Klick dein erstes, oder ein weiteres Event erstellen.', side: "bottom", align: 'start' }
        },
        {
          element: '.tour-tabs',
          popover: { title: 'Deine Übersicht', description: 'Wechsle hier ganz einfach zwischen deinen eigenen Events und solchen, zu denen du eingeladen wurdest.', side: "bottom", align: 'start' }
        },
        {
          element: '.tour-event-card',
          popover: { title: 'Deine Events', description: 'Hier siehst du deine Events. Klicke auf die Kachel, um es genauer zu verwalten.', side: "top", align: 'start' }
        },
        {
          element: '.tour-profile',
          popover: { title: 'Dein Profil', description: 'In deinem Profil kannst du dein Abo-Modell anpassen, Kontodetails ändern oder dich abmelden.', side: "left", align: 'start' }
        }
      ],
      onDestroyed: () => {
        localStorage.setItem('hasSeenDashboardTutorial', 'true');
      }
    });

    driverObj.drive();
  };

  useEffect(() => {
    const hasSeenTutorial = localStorage.getItem('hasSeenDashboardTutorial');
    if (!hasSeenTutorial) {
      setTimeout(() => startTutorial(), 1000);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
    fetchLogo();
  }, []);

  useEffect(() => {
    if (nextEvent) {
      const updateCountdown = () => {
        const eventDate = parseISO(nextEvent.date);
        const now = new Date();
        if (eventDate > now) {
          setCountdown({
            days: Math.max(0, differenceInDays(eventDate, now)),
            hours: Math.max(0, differenceInHours(eventDate, now) % 24),
            minutes: Math.max(0, differenceInMinutes(eventDate, now) % 60),
          });
        }
      };
      updateCountdown();
      const timer = setInterval(updateCountdown, 1000);
      return () => clearInterval(timer);
    }
  }, [nextEvent]);

  const fetchLogo = async () => {
    try {
      const response = await authGet(`/branding/logo`);
      setLogo(response.data.logo_url);
    } catch (error) {
      console.error("Failed to fetch logo:", error);
    }
  };

  const fetchDashboardData = async () => {
    try {
      // Fetch both created events and participations
      const [eventsRes, participationsRes] = await Promise.all([
        authGet(`/events`),
        authGet(`/my-participations`)
      ]);

      const allEvents = Array.isArray(eventsRes.data) ? eventsRes.data : [];
      setEvents(allEvents);

      const allParticipations = Array.isArray(participationsRes.data) ? participationsRes.data : [];
      setParticipations(allParticipations);

      const combinedUpcoming = [...allEvents, ...allParticipations]
        .filter(e => new Date(e.date) > new Date())
        .sort((a, b) => new Date(a.date) - new Date(b.date))[0];

      if (combinedUpcoming) {
        setNextEvent(combinedUpcoming);
      } else {
        setNextEvent(null);
      }
    } catch (error) {
      console.error("Dashboard error:", error);
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEvent = async (e, eventId) => {
    e.stopPropagation(); // Prevent navigation to event detail page

    if (!confirm("Möchten Sie dieses Event wirklich endgültig löschen? Alle Daten gehen verloren.")) {
      return;
    }

    try {
      await authDelete(`/events/${eventId}`);
      toast.success("Event gelöscht");
      fetchDashboardData();
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Löschen fehlgeschlagen");
    }
  };

  const handleInvitationAction = async (e, eventId, action, message = null) => {
    e.stopPropagation();
    try {
      if (action === 'delete') {
        if (!confirm("Einladung wirklich entfernen?")) return;

        try {
          // Use the correct endpoint for removing participation
          await authDelete(`/my-participations/${eventId}`);
        } catch (err) {
          console.error("Delete failed, falling back to decline", err);
          // Fallback to declining if delete fails
          await authPut(`/my-participations/${eventId}/rsvp`, { status: 'declined' });
        }
        setParticipations(prev => prev.filter(p => p.id !== eventId));
        toast.success("Einladung entfernt");
      } else {
        // RSVP using the correct PUT endpoint
        const payload = { status: action };
        if (message) payload.message = message;

        await authPut(`/my-participations/${eventId}/rsvp`, payload);
        // Update local state
        setParticipations(prev => prev.map(p =>
          p.id === eventId ? { ...p, my_rsvp_status: action } : p
        ));
        toast.success(action === 'confirmed' ? "Zugesagt!" : "Abgesagt.");
        setDecliningEventId(null);
        setDeclineMessage("");
      }
    } catch (error) {
      console.error("Action failed:", error);
      toast.error("Aktion fehlgeschlagen.");
    }
  };

  // Helper to construct image URL robustly
  const getImageUrl = (imagePath, eventType) => {
    if (!imagePath) {
      // Return type-specific default image if no cover image
      return EVENT_TYPE_IMAGES[eventType?.toLowerCase()] || EVENT_TYPE_IMAGES.other || "/images/headers/party_new.png";
    }

    // Absolute URL (e.g. Unsplash or data:image)
    if (imagePath.startsWith('http') || imagePath.startsWith('data:')) return imagePath;

    let path = imagePath;

    // Handle legacy "uploads/" paths by converting to API route
    if (path.includes('uploads/')) {
      const parts = path.split('uploads/');
      path = `/api/media/${parts[parts.length - 1]}`;
    }

    // Ensure leading slash
    if (!path.startsWith('/')) {
      path = `/${path}`;
    }

    // Connect to backend
    // Add cache buster to prevent caching 404s
    const url = `${BACKEND_URL}${path}`;
    // Only add cache buster if it's an API media URL
    if (url.includes('/api/media/')) {
      return `${url}?v=${cacheBuster}`;
    }
    return url;
  };

  if (loading) return <div className="min-h-screen bg-brand-background flex items-center justify-center"><div className="w-10 h-10 border-4 border-brand-accent border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-brand-background text-brand-text font-['Plus_Jakarta_Sans'] pb-40 selection:bg-brand-accent/20">
      <Toaster position="top-center" richColors />

      {/* Header */}
      <header className="px-6 py-8 max-w-[1400px] mx-auto flex items-center justify-between sticky top-0 z-50 bg-brand-background/80 backdrop-blur-md">
        <div className="flex items-center gap-4">
          {logo ? (
            <img
              src={`${BACKEND_URL}${logo}`}
              alt="Logo"
              className="h-10 md:h-12 w-auto object-contain"
              style={{ transform: 'scale(var(--logo-scale, 1))' }}
            />
          ) : (
            <div className="w-12 h-12 bg-brand-accent rounded-2xl flex items-center justify-center text-white shadow-lg shadow-brand-accent/20">
              <Sparkles className="w-6 h-6" />
            </div>
          )}
          <div className="hidden sm:block">
            {/* Adjusted header text as requested: removed 'Mokiplaner' title */}
            <p className="text-xl font-black text-brand-accent uppercase tracking-[0.2em] opacity-90">Dashboard</p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <button
            onClick={() => {
              localStorage.removeItem('hasSeenDashboardTutorial');
              startTutorial();
            }}
            className="w-12 h-12 bg-white rounded-xl border border-gray-100 shadow-sm flex items-center justify-center text-gray-400 hover:text-brand-accent hover:border-brand-accent transition-all"
            title="Tutorial starten"
          >
            <HelpCircle className="w-5 h-5" />
          </button>
          <button
            onClick={() => navigate('/event/new')}
            className="tour-new-event bg-brand-text text-white px-8 py-4 rounded-2xl font-black uppercase text-[10px] tracking-[0.2em] hover:bg-brand-accent transition-all shadow-xl flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Neu
          </button>
          <button
            onClick={() => navigate('/profile')}
            className="tour-profile w-12 h-12 bg-white rounded-xl border border-gray-100 shadow-sm flex items-center justify-center overflow-hidden hover:border-brand-accent transition-all"
          >
            {user?.avatar ? <img src={user.avatar} className="w-full h-full object-cover" /> : <User className="w-5 h-5 text-gray-400" />}
          </button>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 py-6 space-y-12">
        {/* Welcome Section */}
        <div className="flex flex-col md:flex-row justify-between items-end gap-8 border-b border-brand-text/5 pb-10">
          <div className="space-y-4">
            <h2 className="text-4xl md:text-6xl font-black tracking-tighter text-brand-text">
              Hallo, <span className="text-brand-accent">{user?.name?.split(' ')[0] || 'Planer'}</span>.
            </h2>
            <p className="text-lg text-gray-500 font-medium max-w-xl">
              Du hast <span className="text-brand-text font-bold">{events.length} eigene Events</span> und {participations.length > 0 ? <><span className="text-brand-text font-bold">{participations.length} Einladungen</span></> : 'keine Einladungen'}.
              {nextEvent && ` Dein nächstes Event ist in ${countdown.days} Tagen.`}
            </p>
          </div>

          <div className="flex gap-4">
            <div
              onClick={() => navigate('/pricing')}
              className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-xl min-w-[140px] cursor-pointer hover:border-brand-accent hover:shadow-2xl transition-all group"
            >
              <p className="text-[10px] font-black text-brand-accent uppercase tracking-widest mb-1">Status</p>
              <h4 className="text-3xl font-black text-brand-text capitalize group-hover:text-brand-accent transition-colors">{user.package_tier || 'Free'}</h4>
            </div>

            {(user.is_admin || user.role === 'admin') && (
              <button
                onClick={() => navigate('/admin')}
                className="bg-brand-text p-6 rounded-[2rem] border border-gray-800 shadow-xl min-w-[140px] text-left hover:bg-brand-accent transition-all group"
              >
                <p className="text-[10px] font-black text-brand-accent group-hover:text-white uppercase tracking-widest mb-1">Backend</p>
                <div className="flex items-center justify-between">
                  <h4 className="text-3xl font-black text-white">Admin</h4>
                  <Shield className="w-5 h-5 text-brand-accent group-hover:text-white" />
                </div>
              </button>
            )}
          </div>
        </div>

        {/* Tabs & Content */}
        <div className="space-y-6">
          {/* Tabs Navigation */}
          <div className="tour-tabs flex items-center gap-4 border-b border-brand-text/10 pb-1">
            <button
              onClick={() => setActiveTab('my-events')}
              className={`text-lg font-black uppercase tracking-widest pb-3 transition-colors ${activeTab === 'my-events' ? 'text-brand-text border-b-2 border-brand-accent' : 'text-gray-400 hover:text-brand-text'}`}
            >
              Meine Events
            </button>
            <button
              onClick={() => setActiveTab('invitations')}
              className={`text-lg font-black uppercase tracking-widest pb-3 transition-colors flex items-center gap-2 ${activeTab === 'invitations' ? 'text-brand-text border-b-2 border-brand-accent' : 'text-gray-400 hover:text-brand-text'}`}
            >
              Einladungen
              {participations.length > 0 && (
                <span className={`text-[9px] px-2 py-0.5 rounded-full ${activeTab === 'invitations' ? 'bg-brand-accent text-white' : 'bg-gray-200 text-gray-500'}`}>
                  {participations.length}
                </span>
              )}
            </button>
          </div>

          {/* MEINE EVENTS TAB */}
          {activeTab === 'my-events' && (
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-300">
              <div className="flex items-center justify-between mb-6">
                <span className="text-sm font-medium text-gray-500">{events.length} Events gefunden</span>
                <div className="flex gap-2">
                  <button className="p-2 text-brand-accent bg-brand-accent/10 rounded-lg"><LayoutGrid className="w-5 h-5" /></button>
                </div>
              </div>

              {events.length === 0 ? (
                <div className="text-center py-20 bg-white rounded-[3rem] border border-gray-100 border-dashed">
                  <Sparkles className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-900">Keine eigenen Events gefunden</h3>
                  <p className="text-gray-500 mb-8">Starte jetzt mit deinem ersten Event.</p>
                  <button onClick={() => navigate('/event/new')} className="bg-brand-accent text-white px-8 py-3 rounded-xl font-bold hover:shadow-lg transition-all">Event erstellen</button>
                </div>
              ) : (
                <div className="tour-event-card grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {events.map((event) => (
                    <motion.div
                      key={event.id}
                      whileHover={{ y: -5 }}
                      onClick={() => navigate(`/event/${event.id}`)}
                      className="group bg-white rounded-[2.5rem] border border-gray-100 overflow-hidden cursor-pointer shadow-[0_20px_40px_-10px_rgba(0,0,0,0.05)] hover:shadow-[0_30px_60px_-15px_rgba(193,154,107,0.15)] hover:border-brand-accent/30 transition-all"
                    >
                      <div className="relative h-64 overflow-hidden">
                        <img
                          src={getImageUrl(event.cover_image, event.event_type)}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                          onError={(e) => {
                            console.error(`Failed to load image for event ${event.id}:`, e.target.src);

                            // Safety Fallback Logic
                            const typeKey = event.event_type?.toLowerCase() || 'other';
                            const fallback = EVENT_TYPE_IMAGES[typeKey] || EVENT_TYPE_IMAGES.other;

                            // Check if we are already displaying the fallback or specific hardcoded URLs
                            const isAlreadyFallback = e.target.src.includes('images/headers');

                            if (!isAlreadyFallback) {
                              e.target.src = fallback;
                            } else {
                              // Ultimate safety net: Local generic party image
                              e.target.src = "/images/headers/party_new.png";
                            }
                          }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-60" />
                        <div className="absolute bottom-6 left-6 right-6 text-white">
                          <span className="bg-white/20 backdrop-blur-md px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border border-white/20">
                            {EVENT_TYPE_TRANSLATIONS[event.event_type] || event.event_type || "Event"}
                          </span>
                          <h4 className="text-2xl font-black mt-3 leading-tight">{event.title}</h4>
                        </div>
                        {/* Action Buttons */}
                        <div className="absolute top-4 right-4 flex gap-2 z-40">
                          {/* Upload Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/event/${event.id}/upload`);
                            }}
                            className="w-10 h-10 bg-brand-accent shadow-[0_10px_20px_rgba(193,154,107,0.4)] rounded-full text-white hover:bg-yellow-600 transition-all flex items-center justify-center border-2 border-white"
                          >
                            <Upload className="w-4 h-4" />
                          </button>
                          {/* Delete Button */}
                          <button
                            onClick={(e) => handleDeleteEvent(e, event.id)}
                            className="w-10 h-10 bg-red-600 shadow-[0_10px_20px_rgba(220,38,38,0.4)] rounded-full text-white hover:bg-red-700 transition-all flex items-center justify-center border-2 border-white"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <div className="p-8 space-y-6">
                        <div className="flex justify-between items-center text-sm font-medium text-gray-500">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-brand-accent" />
                            {event.date ? format(parseISO(event.date), "dd. MMM yyyy", { locale: de }) : "Kein Datum"}
                          </div>
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-brand-accent" />
                            {event.location?.split(',')[0] || "Location"}
                          </div>
                        </div>

                        <div className="pt-6 border-t border-gray-50 flex items-center justify-between">
                          <div className="flex flex-col">
                            <div className="flex items-center gap-2 text-xs font-bold text-gray-900 uppercase tracking-wider">
                              <Users className="w-4 h-4 text-brand-accent" />
                              {event.guest_stats?.confirmed || 0} Zusagen
                            </div>
                            <div className="text-[10px] font-medium text-gray-400 ml-6">
                              von {event.guest_count || 0} Gästen
                            </div>
                          </div>
                          <div className="w-10 h-10 rounded-full bg-brand-background flex items-center justify-center group-hover:bg-brand-accent group-hover:text-white transition-colors">
                            <ChevronRight className="w-5 h-5" />
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* INVITATIONS TAB */}
          {activeTab === 'invitations' && (
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-300">
              {participations.length === 0 ? (
                <div className="text-center py-20 bg-white rounded-[3rem] border border-gray-100 border-dashed">
                  <Sparkles className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-900">Keine offenen Einladungen</h3>
                  <p className="text-gray-500">Du hast aktuell keine Einladungen zu anderen Events.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {participations.map((event) => (
                    <motion.div
                      key={event.id}
                      whileHover={{ y: -5 }}
                      onClick={() => navigate(`/e/${event.share_code || event.id}`)}
                      className="group bg-white rounded-[2.5rem] border border-gray-100 overflow-hidden cursor-pointer shadow-[0_20px_40px_-10px_rgba(0,0,0,0.05)] hover:shadow-[0_30px_60px_-15px_rgba(193,154,107,0.15)] hover:border-brand-accent/30 transition-all flex flex-col justify-between h-full"
                    >
                      <div>
                        {/* Image Header */}
                        <div className="relative h-56 overflow-hidden">
                          <img
                            src={getImageUrl(event.cover_image, event.event_type)}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                            onError={(e) => {
                              console.error(`Failed to load invitation image for event ${event.id}:`, e.target.src);
                              const typeKey = event.event_type?.toLowerCase() || 'other';
                              const fallback = EVENT_TYPE_IMAGES[typeKey] || EVENT_TYPE_IMAGES.other;
                              if (!e.target.src.includes('images/headers')) {
                                e.target.src = fallback;
                              } else {
                                e.target.src = "/images/headers/party_new.png";
                              }
                            }}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-60" />

                          {/* Status Badge */}
                          <div className="absolute top-4 right-4 z-10">
                            {event.my_rsvp_status === 'confirmed' && (
                              <span className="bg-emerald-500 text-white px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest shadow-lg flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" /> Zugesagt
                              </span>
                            )}
                            {event.my_rsvp_status === 'declined' && (
                              <span className="bg-red-500 text-white px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest shadow-lg flex items-center gap-1">
                                <XCircle className="w-3 h-3" /> Abgesagt
                              </span>
                            )}
                            {(!event.my_rsvp_status || event.my_rsvp_status === 'pending') && (
                              <span className="bg-amber-400 text-white px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest shadow-lg">
                                Ausstehend
                              </span>
                            )}
                          </div>

                          <div className="absolute bottom-6 left-6 right-6 text-white">
                            <h4 className="text-xl font-black leading-tight mb-2">{event.title}</h4>
                            <p className="text-[10px] font-bold text-white/80 uppercase tracking-widest flex items-center gap-1">
                              <User className="w-3 h-3" /> Von {event.owner_name || "Unbekannt"}
                            </p>
                          </div>
                        </div>

                        {/* Details */}
                        <div className="p-6">
                          <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                            <Calendar className="w-4 h-4 text-brand-accent" />
                            <span className="font-medium">{event.date ? format(parseISO(event.date), "dd. MMM yyyy", { locale: de }) : "Kein Datum"}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <MapPin className="w-4 h-4 text-brand-accent" />
                            <span className="font-medium">{event.location || "Online"}</span>
                          </div>
                        </div>
                      </div>

                      {/* ACTION BUTTONS */}
                      <div className="p-4 border-t border-gray-100 bg-gray-50/50">
                        {(() => {
                          if (decliningEventId === event.id) {
                            return (
                              <div className="animate-in fade-in zoom-in-95 space-y-3">
                                <textarea
                                  onClick={(e) => e.stopPropagation()}
                                  value={declineMessage}
                                  onChange={(e) => setDeclineMessage(e.target.value)}
                                  placeholder="Möchtest du eine Nachricht hinterlassen? (Optional)"
                                  className="w-full text-sm p-3 rounded-xl border border-gray-200 focus:outline-none focus:border-brand-accent min-h-[80px]"
                                  autoFocus
                                />
                                <div className="flex gap-2">
                                  <button
                                    onClick={(e) => handleInvitationAction(e, event.id, 'declined', declineMessage)}
                                    className="flex-1 py-3 bg-brand-text text-white rounded-xl text-xs font-bold uppercase tracking-wider hover:bg-black transition-colors"
                                  >
                                    Absenden
                                  </button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setDecliningEventId(null); }}
                                    className="px-4 py-3 bg-white border border-gray-200 text-gray-600 rounded-xl text-xs font-bold uppercase tracking-wider hover:bg-gray-100 transition-colors"
                                  >
                                    Zurück
                                  </button>
                                </div>
                              </div>
                            );
                          }

                          if (event.my_rsvp_status === 'confirmed') {
                            return (
                              <div className="flex items-center justify-between bg-emerald-50 p-4 rounded-xl border border-emerald-100">
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600">
                                    <Check className="w-4 h-4" />
                                  </div>
                                  <div>
                                    <p className="text-xs font-bold text-emerald-800 uppercase tracking-wider">Dabei!</p>
                                    <p className="text-[10px] text-emerald-600 font-medium">Du hast zugesagt.</p>
                                  </div>
                                </div>
                                <button
                                  onClick={(e) => { e.stopPropagation(); setDecliningEventId(event.id); setDeclineMessage(""); }}
                                  className="text-[10px] font-bold text-emerald-600 underline hover:text-emerald-800"
                                >
                                  Doch absagen?
                                </button>
                              </div>
                            );
                          }

                          if (event.my_rsvp_status === 'declined') {
                            return (
                              <div className="flex items-center justify-between bg-gray-100 p-4 rounded-xl border border-gray-200">
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-500">
                                    <X className="w-4 h-4" />
                                  </div>
                                  <div>
                                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Abgesagt</p>
                                    <p className="text-[10px] text-gray-400 font-medium">Schade, dass du nicht kannst.</p>
                                  </div>
                                </div>
                                <div className="flex gap-2">
                                  <button
                                    onClick={(e) => handleInvitationAction(e, event.id, 'confirmed')}
                                    className="text-[10px] font-bold text-gray-500 underline hover:text-brand-accent"
                                  >
                                    Ändern?
                                  </button>
                                  <button
                                    onClick={(e) => handleInvitationAction(e, event.id, 'delete')}
                                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-white border border-gray-200 text-gray-400 hover:text-red-500 hover:border-red-200 transition-colors"
                                    title="Aus Liste entfernen"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            );
                          }

                          // Default: Pending or No Status
                          return (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={(e) => handleInvitationAction(e, event.id, 'confirmed')}
                                className="flex-1 py-3 rounded-xl font-bold text-xs uppercase tracking-wider flex items-center justify-center gap-2 transition-all bg-emerald-500 text-white hover:bg-emerald-600 shadow-lg shadow-emerald-200"
                              >
                                <Check className="w-4 h-4" /> Zusagen
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); setDecliningEventId(event.id); setDeclineMessage(""); }}
                                className="flex-1 py-3 rounded-xl font-bold text-xs uppercase tracking-wider flex items-center justify-center gap-2 transition-all bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
                              >
                                <X className="w-4 h-4" /> Absagen
                              </button>
                            </div>
                          );
                        })()}
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
